import React, { useMemo } from "react";
import { selectionHolds } from "helpers/rowSelectionMemo";
import { rowIsIncomplete } from "../../helpers/treeRowUids";
import { message as antdMessage } from "antd";
import { MDBBox, MDBIcon } from "mdbreact";
import { ReactComponent as Dollar } from "../../assets/images/dollar2.svg";
import { ReactComponent as IconMesh } from "../../assets/images/mesh.svg";
import { checkDepth } from "../../helpers/checkDepth";
import { meshState, MESH_STATE_TITLE } from "../../helpers/lazyCategoryGeometry";
import {
  findRecursiveInTree,
  UpdateGroupVisibility,
} from "../../helpers/three";
import {
  useStreamingTreeNode,
  extractLeafUids,
} from "../../hooks/useStreamingTreeNode";

import "./TreeViewerItem.scss";

// === EYE-ICON ARCHITECTURE ===
//
// Two paths, chosen per-row at render time based on whether streaming
// is active for the current viewer:
//
//   1. STREAMING — viewer.streaming.state exists.
//      Eye click → useStreamingTreeNode.toggleHidden(), which calls
//      store.hideGroup(node.id, leafUids) for sections or
//      store.hide(uid) for leaves. Single emit, single applicator
//      pass, atomic across 10k+ leaves. UI eye state reads directly
//      from the store via the hook → cannot desync from geometry.
//
//   2. LEGACY — viewer.streaming is undefined (legacy non-streaming
//      projects). Eye click runs the original recursive
//      treeVisible-mutate + UpdateGroupVisibility path. Unchanged
//      behavior for projects without manifests.
//
// Selection (click / right-click) is unchanged here; it routes
// through WebGL.current.viewer.onClickChildInList which is
// streaming-aware on its own end.
//
// === WHY DELETE THE OLD RECURSIVE EYE LOGIC ===
//
// The old eye click did:
//   - mutate this node's treeVisible
//   - walk up the path; for every ancestor where SOME complicated
//     condition matched, recurse-disable() its entire subtree
//
// That ancestor recursion has two failure modes that match the bugs
// reported on the streaming side:
//   1. SLOW — for a section with 10k descendants, the recursion fires
//      UpdateGroupVisibility per leaf, each setting needsUpdate on
//      instanceMatrix. No batching → seconds of work.
//   2. CROSS-SECTION DESYNC — the predicate
//        item.nodes.filter(el => el.treeVisible === false).length
//          >= item.nodes.length - 1
//      treats sibling sections as influence on this section's state.
//      Hide section A (which marks A.treeVisible=false), then click
//      section B's eye → B's path-walk sees A.treeVisible=false and
//      may recurse-disable B's children even though we just clicked
//      to UN-hide. UI flag and geometry diverge.
//
// In the streaming path, store.hideGroup / store.showGroup are atomic
// and group-scoped. There is no cross-group bleed because each
// group's contribution to the hidden set is tracked separately. UI
// state reads from the store, so it cannot disagree with geometry.

const TreeViewerItem = (props) => {
  const {
    item: { hasNodes, level, meshInfo, isOpen, treeVisible, id, label, key },
    selectGroups,
    setSelectGroups,
    clickedChild,
    setClickedChild,
    WebGL,
    tree,
    setTreeData,
    index,
    setOpenNodes,
    selectors,
    isUnmatchedMeshes,
    isExistingMeshes,
    selectedMesh,
    isTableView = false,
    // Optional callback fired when this row is clicked. Receives the
    // ctrlKey state of the click event so the parent can decide whether
    // to enforce "single tree at a time" semantics (clear other trees'
    // selections when ctrl is NOT held). Used by TimelineViewer where
    // five sibling trees coexist; safe to omit on viewers with one tree.
    onSelectionStart,
    // Rows built from a document's index stand for elements whose
    // records have not been read yet (helpers/recordIndexTree.js).
    // Opening, selecting or hiding such a row needs them, so the page
    // hands us the loader; it resolves once the records are in the tree.
    onExpandLazy,
    // A section row's elements when its own rows cannot list them yet
    // (helpers/treeRowUids): async node -> { uids, source, complete }.
    onResolveRowUids,
    // A file's root row: docId -> the UniqueId prefixes of that file, so
    // its eye hides the whole file (ViewerStateStore.hideDocument).
    onDocumentPrefixes,
    // Why it could not: "prefixes-shared" (this file's element ids are
    // shared with another file, and no amount of waiting changes that) or
    // "prefixes-unknown" (we never learned them - loading, or a failed
    // read). The advice differs, so the row must not give one for the
    // other. Optional: without it the row says the waiting version.
    onWhyNotHideable,
    // A file the export holds NO elements for. It is a third answer, not a
    // shade of "unknown": there is nothing to wait for, so the row must not
    // send the person back to wait. Optional, like the two above.
    onDocumentIsEmpty,
    // ONE ICON STOOD FOR TWO DIFFERENT FACTS. checkMesh(uid) is only
    // "meshesObject has this uid", so on a streaming export the mesh icon
    // appeared both for an element this export never placed AND for one
    // nobody has fetched yet - and it read as "missing" either way.
    //
    // A page that asks for a branch's geometry (helpers/lazyCategoryGeometry)
    // passes askedUids and untiledUids, and the row then says which of the
    // three it is. Both optional: without them the icon means exactly what it
    // meant before, which is what every page that has not been taught this
    // still wants.
    askedUids,
    untiledUids,
  } = props;
  // props.meshVersion is deliberately NOT destructured: it is read only by the
  // memo comparator at the bottom of this file. Bumped when the viewer's
  // residency changes, it is what makes a row whose geometry has just arrived
  // re-render - the comparator is otherwise right that nothing about the row
  // has changed, and the icon would sit there until the next click.

  // WHICH OF THE THREE FACTS this row's icon stands for (helpers/
  // lazyCategoryGeometry). Without askedUids/untiledUids from the page this is
  // "built" or "absent", exactly the two states the icon had before.
  const rowUid = meshInfo?.UniqueId;
  const rowMeshState = rowUid
    ? meshState(rowUid, {
      hasMesh: !!(WebGL?.current?.viewer?.checkMesh && WebGL.current.viewer.checkMesh(rowUid)),
      asked: askedUids ? askedUids.has(rowUid) : false,
      untiled: untiledUids ? untiledUids.has(rowUid) : false,
    })
    : "built";

  // Resolve the FULL subtree node for this row.
  //
  // `props.item` is what react-simple-tree-menu hands us — a flattened
  // entry with `{ id, key, label, level, hasNodes, isOpen, meshInfo? }`.
  // Critically it does NOT carry the `nodes` array; the children live
  // separately in the flattened items list. So extractLeafUids(props.item)
  // returns []  for any section row, which made the section eye toggle a
  // no-op (only leaves were hiding/showing).
  //
  // The original tree (still keyed by node.key) is passed in via `tree`,
  // so we walk it with findRecursiveInTree to get the real subtree node
  // — that one DOES have `nodes`. extractLeafUids on that returns every
  // descendant uid, and store.hideGroup / store.bulkShow then act on
  // the whole subtree, which is the recursive behavior the user expects.
  //
  // Memoized on (tree, id) so the resolved node ref stays stable across
  // re-renders, which is what useStreamingTreeNode's leafUids cache
  // depends on (it invalidates only when node ref changes).
  const resolvedNode = useMemo(() => {
    if (!tree) return props.item;
    try {
      const found = findRecursiveInTree(
        Array.isArray(tree) ? tree : [tree],
        id
      );
      if (found?.result) return found.result;
    } catch (e) {
      // Defensive — if tree shape diverges (e.g. an empty section was
      // pushed into treeFilesData), fall back to the flat item rather
      // than crashing the row.
    }
    return props.item;
  }, [tree, id, props.item]);

  // Nothing below can act on a row whose elements are still in the
  // worker: the tree walks read `nodes`, and an unopened row has only a
  // placeholder. So every path that needs them waits for them first.
  const ensureLoaded = async () => {
    if (!onExpandLazy) return;
    if (!resolvedNode?.lazy || resolvedNode.lazy.loaded) return;
    try {
      await onExpandLazy(resolvedNode);
    } catch (e) {
      // The row shows why; the click just does nothing.
    }
  };

  // The hook is viewer-agnostic. When streaming.state isn't set up
  // (legacy projects), isStreamingActive is false and operations
  // are no-ops. We use that flag to decide which eye-click path runs.
  const {
    isHidden: streamingIsHidden,
    toggleHidden: streamingToggleHidden,
    isStreamingActive,
  } = useStreamingTreeNode({
    viewer: WebGL?.current?.viewer,
    node: resolvedNode,
    getLeafUids: extractLeafUids,
  });

  const depthClassName = checkDepth(level);

  // Eye-icon visual state:
  //   streaming → read from store (via hook)
  //   legacy → read from item.treeVisible (unchanged)
  const eyeOpen = isStreamingActive ? !streamingIsHidden : !!treeVisible;

  const itemClassName = `tree-item d-flex user-select-none
    border-bottom border-light ${isTableView ? "tree-item-table-view" : ""}
    ${index === 0 ? "border-top" : ""} w-100
    align-items-center ${!hasNodes ? "cursor-pointer" : ""}
    ${isOpen && !selectGroups?.includes(id) ? "grey lighten-3" : ""}
    ${
      meshInfo &&
      clickedChild.find((child) => child.UniqueId === meshInfo.UniqueId)
        ? "amber light-2"
        : ""
    }
    ${selectGroups?.includes(id) ? "amber light-2" : ""}
    ${depthClassName}`.trimRight();

  // Selection / multi-select logic — unchanged from the original
  // file. Routes through WebGL.current.viewer.onClickChildInList,
  // which on the streaming side is wrapped to go through store
  // batching for highlight emits.
  const selectElements = (meshList, list, group) => {
    const temp = [...tree];
    const newTreeItem = findRecursiveInTree(temp, id);

    if (
      (!newTreeItem.result.nodes || newTreeItem.result.nodes.length === 0) &&
      !newTreeItem.result.meshInfo?.UniqueId
    ) {
      return;
    }

    if (
      (selectGroups?.includes(newTreeItem.result?.id) ||
        clickedChild.filter(
          (item) => item.UniqueId === newTreeItem.result.meshInfo?.UniqueId
        ).length > 0) &&
      ((!group &&
        (!selectGroups?.includes(newTreeItem.parent?.id) ||
          newTreeItem.parent.nodes.length < 2)) ||
        group)
    ) {
      hasNodes
        ? (list = list.filter((item) => item !== newTreeItem.result.id))
        : (meshList = meshList.filter(
            (item) => item.UniqueId !== newTreeItem.result.meshInfo?.UniqueId
          ));

      newTreeItem.path.forEach((el) => {
        el.nodes.length > 0
          ? (list = list.filter((item) => item !== el.id))
          : (meshList = meshList.filter(
              (item) => item.UniqueId !== el.meshInfo?.UniqueId
            ));
      });

      if (
        newTreeItem.result.nodes.filter(
          (el) =>
            list?.includes(el.id) ||
            meshList.filter((item) => item.UniqueId === el.meshInfo?.UniqueId)
              .length > 0
        ).length >= newTreeItem.result.nodes.length
      ) {
        (function enable(tree) {
          if (tree.nodes && tree.nodes.length > 0)
            list = list.filter((item) => item !== tree.id);
          if (tree.meshInfo?.UniqueId)
            meshList.filter(
              (item) => item.UniqueId !== tree.meshInfo?.UniqueId
            );

          tree.nodes.forEach((item) => enable(item));
        })(newTreeItem.result);
      }
    } else {
      hasNodes
        ? list.push(newTreeItem.result.id)
        : meshList.push(newTreeItem.result.meshInfo);

      newTreeItem.path.forEach((item) => {
        if (
          list?.includes(item.id) ||
          (item.nodes.filter((el) => list?.includes(el.id)).length >=
            item.nodes.length - 1 &&
            (!newTreeItem.parent ||
              newTreeItem.parent.nodes.filter((el) => list?.includes(el.id))
                .length >= newTreeItem.parent.nodes.length)) ||
          (item.nodes.filter(
            (el) =>
              meshList.filter(
                (item) => item.UniqueId === el.meshInfo?.UniqueId
              ).length > 0
          ).length >=
            item.nodes.length - 1 &&
            (!newTreeItem.parent ||
              newTreeItem.parent.nodes.filter(
                (el) =>
                  meshList.filter(
                    (item) => item.UniqueId === el.meshInfo?.UniqueId
                  ).length > 0
              ).length >= newTreeItem.parent.nodes.length))
        ) {
          (function disable(tree) {
            if (
              !list?.includes(tree.id) &&
              meshList.filter((el) => el.UniqueId === tree.meshInfo?.UniqueId)
                .length === 0
            ) {
              if (tree.nodes && tree.nodes.length > 0) list.push(tree.id);
              if (tree.meshInfo?.UniqueId) meshList.push(tree.meshInfo);
            }
            tree.nodes.forEach((item) => disable(item));
          })(item);
        }
      });
    }

    // === BATCHED SELECTION ===
    //
    // For 10k+ uid selections (whole subtree under a section),
    // wrap the per-uid onClickChildInList loop in a store batch so
    // all the highlight emits coalesce into a single applicator
    // pass. Without this, every onClickChildInList call would emit
    // its own 'highlight' event → applicator runs N times.
    //
    // beginBatch/endBatch are inert when the store isn't active
    // (legacy projects), so this is safe to call unconditionally.
    const store = WebGL.current.viewer.streaming?.state;
    const batchActive = !!store;

    if (batchActive) store.beginBatch();
    try {
      WebGL.current.viewer.onClickChildInList(
        "reset",
        false,
        selectors,
        label === "unknown",
        false // shouldFrameCamera — we frame at the end
      );

      meshList.forEach((item) => {
        WebGL.current.viewer.onClickChildInList(
          item.UniqueId,
          true,
          selectors,
          label === "unknown",
          false // shouldFrameCamera
        );
        selectedMesh.current = item.UniqueId;
      });
    } finally {
      if (batchActive) store.endBatch();
    }

    setSelectGroups(list);
    setClickedChild(meshList);
    requestAnimationFrame(() => {
      try { WebGL.current.viewer.targetItems(); } catch (e) { console.warn('targetItems failed', e); }
    });
  };

  // === EYE-ICON CLICK HANDLER ===
  //
  // Streaming path: hook handles everything. UI re-renders driven
  //   by store subscription (no setTreeData call needed for
  //   visibility — that lives in store now).
  //
  // Legacy path: original recursive treeVisible mutation +
  //   UpdateGroupVisibility, then setTreeData to push the mutation
  //   back to React state.
  //
  // Both paths leave selection / clickedChild / openNodes alone.
  // A row too big to act on from the tree is said so, never half done.
  const tooManyNotice = (r) => {
    antdMessage.warning(
      `${label} has ${Number(r.count || 0).toLocaleString()} elements - too many to hide, select or isolate from the tree at once. Open it and use its groups, or pick in the model.`,
      6
    );
  };

  const onEyeClick = async (e) => {
    e.stopPropagation();

    await ensureLoaded();

    if (isStreamingActive) {
      // A FILE's root row: the whole file, by its UniqueId prefixes. Its
      // rows below are placeholders and the file can hold a million
      // elements, so it is never asked for as a list - and never sent
      // down the category path below, which read the file as its first
      // category and hid that one only.
      if (resolvedNode?.documentRoot) {
        const store = WebGL?.current?.viewer?.streaming?.state;
        if (streamingIsHidden || (store && store.isDocumentHidden && store.isDocumentHidden(id))) {
          streamingToggleHidden();
          return;
        }
        const prefixes = onDocumentPrefixes ? onDocumentPrefixes(resolvedNode.docId) : [];
        if (store && store.hideDocument && prefixes.length && store.hideDocument(id, prefixes)) return;
        // Asked BEFORE the two "could not" messages: an empty file is not a
        // failure to tell files apart, it is a file with nothing in it, and
        // saying so is information rather than a warning.
        if (onDocumentIsEmpty && onDocumentIsEmpty(resolvedNode.docId)) {
          antdMessage.info(`${label} has no elements in this export, so there is nothing to hide.`, 6);
          return;
        }
        const why = onWhyNotHideable ? onWhyNotHideable(resolvedNode.docId) : "prefixes-unknown";
        antdMessage.warning(
          why === "prefixes-shared"
            ? `${label}: this export gives this file the same element ids as another file, so hiding it would hide that one too. Hide its categories instead.`
            : `${label}: this file's elements could not be told apart from the other files yet, so it was not hidden. Try again once the model has finished loading, or hide its categories.`,
          6
        );
        return;
      }
      // A Category or Group row on an index-driven tree: its rows below are
      // still placeholders, so ask for its elements before hiding them.
      if (onResolveRowUids && hasNodes && rowIsIncomplete(resolvedNode)) {
        const r = await onResolveRowUids(resolvedNode);
        if (r && r.tooMany) { tooManyNotice(r); return; }
        if (r && (r.source === "server" || r.source === "records")) resolvedNode.allUids = r.uids;
      }
      streamingToggleHidden();
      return;
    }

    // ---- Legacy fallback (unchanged behavior) -------------------
    const temp = [...tree];
    const newTreeItem = findRecursiveInTree(temp, id);

    if (newTreeItem.result.treeVisible === true) {
      newTreeItem.result.treeVisible = false;
      newTreeItem.path.forEach((item) => {
        if (
          (item.nodes.filter((el) => el.treeVisible === false).length >=
            item.nodes.length - 1 &&
            (!newTreeItem.parent ||
              newTreeItem.parent.nodes.filter(
                (el) => el.treeVisible === false
              ).length >= newTreeItem.parent.nodes.length)) ||
          item.treeVisible === false
        ) {
          (function disable(treeNode) {
            treeNode.treeVisible = false;
            if (treeNode.meshInfo) {
              const temp = WebGL.current.viewer.meshesObject.get(
                treeNode.meshInfo.UniqueId
              );
              if (temp) UpdateGroupVisibility(temp, false);
            }
            treeNode.nodes.forEach((item) => disable(item));
          })(item);
        }
      });
    } else {
      newTreeItem.result.treeVisible = true;
      newTreeItem.path.forEach((item) => {
        item.treeVisible = true;
      });
      if (
        newTreeItem.result.nodes.filter((item) => item.treeVisible === false)
          .length >= newTreeItem.result.nodes.length
      ) {
        (function enable(treeNode) {
          treeNode.treeVisible = true;
          if (treeNode.meshInfo) {
            const temp = WebGL.current.viewer.meshesObject.get(
              treeNode.meshInfo.UniqueId
            );
            if (temp) UpdateGroupVisibility(temp, true);
          }
          treeNode.nodes.forEach((item) => enable(item));
        })(newTreeItem.result);
      }
    }

    setTreeData(temp);
  };

  return (
    <div
      id={id}
      className={itemClassName}
      onContextMenu={(e) => {
        e.preventDefault();
        isUnmatchedMeshes();
        isExistingMeshes();
        ensureLoaded().then(() =>
          selectElements([...clickedChild], [...selectGroups], tree)
        );
      }}
      onClick={(e) => {
        if (onSelectionStart) onSelectionStart(e.ctrlKey);
        isUnmatchedMeshes();
        isExistingMeshes();
        // Plain click → selectElements with empty cumulative, which
        // reduces to "select just this row" inside selectElements'
        // computed-state logic. Ctrl click → selectElements with the
        // current selection, which extends/toggles this row in place.
        //
        // Why not call selectElements([], []) FIRST and then the ctrl
        // version: the first call would reset the viewer (and, while
        // isolate mode is on, its first reset/re-add cycle would
        // re-isolate around just this row). The second call would
        // immediately re-reset and re-isolate around the cumulative
        // selection. Net visual is correct because nothing renders
        // between the two batches in a single click handler, but the
        // applicator runs twice and isolated set briefly collapses
        // to {clickedRow} between them. Doing the right call once
        // skips both the wasted work and that intermediate state —
        // important once isolate mode is sticky.
        const ctrl = e.ctrlKey;
        ensureLoaded().then(async () => {
          // A section whose rows cannot list their elements yet: select
          // what the server says it stands for (select, highlight, and
          // isolate follows the selection), keyed by the row.
          if (onResolveRowUids && hasNodes && isStreamingActive && rowIsIncomplete(resolvedNode)
            && WebGL.current && WebGL.current.viewer && typeof WebGL.current.viewer.selectUids === "function") {
            const r = await onResolveRowUids(resolvedNode);
            if (r && r.tooMany) { tooManyNotice(r); return; }
            if (r && (r.source === "server" || r.source === "records") && r.uids.length) {
              WebGL.current.viewer.selectUids(r.uids, selectors, { add: ctrl });
              setSelectGroups(ctrl ? [...new Set([...(selectGroups || []), id])] : [id]);
              if (!ctrl) setClickedChild([]);
              return;
            }
          }
          if (ctrl) {
            selectElements([...clickedChild], [...selectGroups], tree);
          } else {
            selectElements([], []);
          }
        });
      }}
    >
      {hasNodes ? (
        <MDBIcon
          far
          className="mr-2"
          icon={`${isOpen ? "minus" : "plus"}-square cursor-pointer`}
          onClick={(e) => {
            e.stopPropagation();
            ensureLoaded();
            setOpenNodes((s) => {
              if (s?.includes(key)) {
                return s.filter((nodeKey) => nodeKey !== key);
              }
              return [...s, key];
            });
          }}
        />
      ) : null}
      <MDBBox className="flex-grow-1 d-flex align-items-center text-truncate">
        {label === "unknown" ? (
          <>
            <Dollar />
            {"  "}
            <span className="ml-2">unpriced</span>
          </>
        ) : rowMeshState !== "built" ? (
          <>
            {/* The same icon, but it now SAYS which of the three facts it
                stands for - and an element on its way is drawn faintly, so a
                column of them reads as "coming" rather than "broken". */}
            <span title={MESH_STATE_TITLE[rowMeshState] || ""} style={rowMeshState === "asked" ? { opacity: 0.45 } : undefined}>
              <IconMesh />
            </span>
            {"  "}
            <span className="ml-2" title={MESH_STATE_TITLE[rowMeshState] || ""}>{label}</span>
          </>
        ) : (
          label
        )}
      </MDBBox>
      {!isTableView && (
        <MDBIcon
          icon={eyeOpen ? "eye" : "eye-slash"}
          className="mr-2"
          onClick={onEyeClick}
        />
      )}
    </div>
  );
};

// Memoized to avoid re-rendering rows whose visible state (selected/grouped/open) hasn't changed.
export default React.memo(TreeViewerItem, (prev, next) => {
  if (prev.item?.id !== next.item?.id) return false;
  if (prev.index !== next.index) return false;
  if (prev.WebGL !== next.WebGL) return false;

  // THE COMPARATOR AND THE RENDER MUST AGREE ABOUT THE SHAPE OF THIS PROP.
  // `clickedChild` is an array of element OBJECTS - the row itself tests
  // clickedChild.find(c => c.UniqueId === meshInfo.UniqueId) up at line 176 -
  // but this compared a uid STRING with .includes(), which against objects is
  // always false. So prevClicked and nextClicked were both false for every
  // row, the comparator answered "nothing changed", and a row NEVER
  // re-rendered when the selection moved.
  //
  // It was invisible while the tree carried key={generateId()}: a uuid key
  // made React throw the whole tree away and rebuild it on every render, so
  // rows were recreated from scratch and a stale memo could not matter.
  // Giving the tree a stable key (2026-10-20) stopped the remount and this
  // surfaced at once as "the tree and the viewer no longer communicate".
  // Both were real bugs; one was hiding the other.
  const prevUid = prev.item?.meshInfo?.UniqueId;
  const nextUid = next.item?.meshInfo?.UniqueId;
  const prevClicked = selectionHolds(prev.clickedChild, prevUid);
  const nextClicked = selectionHolds(next.clickedChild, nextUid);
  if (prevClicked !== nextClicked) return false;

  const prevId = prev.item?.id;
  const nextId = next.item?.id;
  const prevGrouped = prevId ? !!prev.selectGroups?.includes(prevId) : false;
  const nextGrouped = nextId ? !!next.selectGroups?.includes(nextId) : false;
  if (prevGrouped !== nextGrouped) return false;

  // RESIDENCY IS A REASON TO RE-RENDER. The mesh icon is read from the viewer
  // (checkMesh) and from the page's asked/untiled sets, none of which this
  // comparator can see. A tile arriving changes what the row should say and
  // nothing it compares, so the row kept its icon until something else
  // happened to move. meshVersion is the page saying "residency moved".
  if (prev.meshVersion !== next.meshVersion) return false;
  if (prev.askedUids !== next.askedUids) return false;
  if (prev.untiledUids !== next.untiledUids) return false;

  return true;
});